import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet } from 'lucide-react';

import { API_BASE_URL, ApiError, api } from '@/lib/api';
import { PieChart, type PieSlice } from '@/components/PieChart';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/use-auth';
import { cn, formatDuration } from '@/lib/utils';

interface UserOption {
  id: string;
  fullName: string;
  email: string;
}

interface ProjectStatsItem {
  projectId: string | null;
  projectName: string;
  color: string;
  minutes: number;
  percentage: number;
  isArchived: boolean;
}

type Scope =
  | { kind: 'self'; userId: string }
  | { kind: 'user'; userId: string; userName: string }
  | { kind: 'team'; userIds: string[] };

interface ProjectStatsResponse {
  from: string;
  to: string;
  scope: Scope;
  totalMinutes: number;
  items: ProjectStatsItem[];
}

interface LocationStatsResponse {
  from: string;
  to: string;
  scope: Scope;
  totalMinutes: number;
  officeMinutes: number;
  remoteMinutes: number;
  officePercentage: number;
  remotePercentage: number;
}

const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

type Period =
  | { mode: 'month'; year: number; month: number }
  | { mode: 'year'; year: number }
  | { mode: 'all' };

function defaultPeriod(): Period {
  const now = new Date();
  return { mode: 'month', year: now.getFullYear(), month: now.getMonth() + 1 };
}

export function Stats() {
  const { user } = useAuth();
  const canPickUser =
    user?.role === 'MANAGER' || user?.role === 'HR_ADMIN' || user?.role === 'SUPER_ADMIN';

  const [period, setPeriod] = useState<Period>(defaultPeriod);
  // userScope: '' = yo | 'all' = equipo | <uuid> = empleado concreto
  const [userScope, setUserScope] = useState<string>('');

  const team = useQuery({
    queryKey: ['team', 'as-options'],
    queryFn: () => api<UserOption[]>('/team'),
    enabled: canPickUser,
  });

  const { from, to, periodLabel } = useMemo(() => {
    if (period.mode === 'all') {
      const now = new Date();
      return {
        from: '2020-01-01',
        to: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
        periodLabel: 'Histórico completo',
      };
    }
    if (period.mode === 'year') {
      const now = new Date();
      const isCurrent = period.year === now.getFullYear();
      const to = isCurrent
        ? `${period.year}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
        : `${period.year}-12-31`;
      return {
        from: `${period.year}-01-01`,
        to,
        periodLabel: `Año ${period.year}`,
      };
    }
    const r = rangeForMonth(period.year, period.month);
    return { ...r, periodLabel: humanRange(period.year, period.month) };
  }, [period]);
  const scopeParam = userScope ? `&userId=${userScope}` : '';

  const projects = useQuery({
    queryKey: ['stats', 'projects', from, to, userScope],
    queryFn: () =>
      api<ProjectStatsResponse>(`/stats/projects?from=${from}&to=${to}${scopeParam}`),
  });

  const location = useQuery({
    queryKey: ['stats', 'location', from, to, userScope],
    queryFn: () =>
      api<LocationStatsResponse>(`/stats/location?from=${from}&to=${to}${scopeParam}`),
  });

  const monthOptions = useMemo(() => {
    const opts: { year: number; month: number; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 13; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      });
    }
    return opts;
  }, []);

  const yearOptions = useMemo(() => {
    const opts: number[] = [];
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 4; y--) opts.push(y);
    return opts;
  }, []);

  const scopeLabel = useMemo(() => {
    if (!userScope) return 'Mi resumen';
    if (userScope === 'all') return 'Equipo completo';
    const u = team.data?.find((x) => x.id === userScope);
    return u?.fullName ?? 'Empleado';
  }, [userScope, team.data]);

  const selectCls =
    'h-9 rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors';

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish">Estadísticas</h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              {scopeLabel} · {periodLabel}
            </p>
          </div>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <select
              value={periodValue(period)}
              onChange={(e) => setPeriod(parsePeriodValue(e.target.value))}
              className={cn(selectCls, 'min-w-[200px]')}
              aria-label="Periodo"
            >
              <option value="all">Histórico completo</option>
              <optgroup label="Por año">
                {yearOptions.map((y) => (
                  <option key={`year-${y}`} value={`year-${y}`}>
                    Año {y}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Por mes">
                {monthOptions.map((o) => (
                  <option key={`month-${o.year}-${o.month}`} value={`month-${o.year}-${o.month}`}>
                    {o.label}
                  </option>
                ))}
              </optgroup>
            </select>
            {canPickUser ? (
              <select
                value={userScope}
                onChange={(e) => setUserScope(e.target.value)}
                className={cn(selectCls, 'min-w-[180px]')}
                aria-label="Usuario"
              >
                <option value="">Mi resumen</option>
                <option value="all">Equipo completo</option>
                <optgroup label="Empleados">
                  {(team.data ?? [])
                    .filter((u) => u.id !== user?.id)
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.fullName}
                      </option>
                    ))}
                </optgroup>
              </select>
            ) : null}
            <ExportExcelButton query={`from=${from}&to=${to}${scopeParam}`} />
          </div>
        </header>

        <div className="p-7 grid grid-cols-1 lg:grid-cols-2 gap-7">
          <ProjectsPanel data={projects.data} loading={projects.isLoading} error={projects.error} />
          <LocationPanel data={location.data} loading={location.isLoading} error={location.error} />
        </div>
      </div>
    </div>
  );
}

/**
 * Descarga el Excel del periodo y la selección actuales. Con "Equipo
 * completo" sale una fila por persona, no la suma.
 */
function ExportExcelButton({ query }: { query: string }) {
  const [busy, setBusy] = useState(false);

  async function handleDownload() {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/stats/export?${query}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: unknown } | null;
        throw new Error(
          typeof body?.message === 'string' ? body.message : 'No se pudo generar el Excel',
        );
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFromDisposition(res.headers.get('Content-Disposition'));
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'No se pudo generar el Excel');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button variant="outline" onClick={() => void handleDownload()} disabled={busy}>
      <FileSpreadsheet className="h-3.5 w-3.5" />
      {busy ? 'Generando…' : 'Descargar Excel'}
    </Button>
  );
}

/** Lee el nombre de `filename*=UTF-8''…` que pone el backend. */
function filenameFromDisposition(header: string | null): string {
  const match = header ? /filename\*=UTF-8''([^;]+)/i.exec(header) : null;
  return match ? decodeURIComponent(match[1]) : 'estadisticas.xlsx';
}

function ProjectsPanel({
  data,
  loading,
  error,
}: {
  data: ProjectStatsResponse | undefined;
  loading: boolean;
  error: unknown;
}) {
  const slices: PieSlice[] = useMemo(
    () =>
      (data?.items ?? []).map((it) => ({
        key: it.projectId ?? 'none',
        label: it.projectName + (it.isArchived ? ' (archivado)' : ''),
        value: it.minutes,
        color: it.color,
        hint: `${formatDuration(it.minutes)} · ${formatPct(it.percentage)}`,
      })),
    [data?.items],
  );
  return (
    <Panel title="Distribución por proyecto" loading={loading} error={error}>
      <ChartLayout
        chart={
          <PieChart
            slices={slices}
            centerLabel={data ? formatDuration(data.totalMinutes) : undefined}
            centerHint="total"
            ariaLabel="Proyectos"
          />
        }
        legend={
          <Legend
            items={
              data?.items.map((it) => ({
                color: it.color,
                label: it.projectName + (it.isArchived ? ' (archivado)' : ''),
                value: formatDuration(it.minutes),
                hint: formatPct(it.percentage),
              })) ?? []
            }
          />
        }
      />
    </Panel>
  );
}

function LocationPanel({
  data,
  loading,
  error,
}: {
  data: LocationStatsResponse | undefined;
  loading: boolean;
  error: unknown;
}) {
  const slices: PieSlice[] = useMemo(() => {
    if (!data) return [];
    return [
      {
        key: 'office',
        label: 'Oficina',
        value: data.officeMinutes,
        color: '#1B23B8',
        hint: `${formatDuration(data.officeMinutes)} · ${formatPct(data.officePercentage)}`,
      },
      {
        key: 'remote',
        label: 'Teletrabajo',
        value: data.remoteMinutes,
        color: '#22c55e',
        hint: `${formatDuration(data.remoteMinutes)} · ${formatPct(data.remotePercentage)}`,
      },
    ];
  }, [data]);
  return (
    <Panel title="Oficina vs teletrabajo" loading={loading} error={error}>
      <ChartLayout
        chart={
          <PieChart
            slices={slices}
            centerLabel={data ? formatDuration(data.totalMinutes) : undefined}
            centerHint="total"
            ariaLabel="Ubicación"
          />
        }
        legend={
          <Legend
            items={
              data
                ? [
                    {
                      color: '#1B23B8',
                      label: 'Oficina',
                      value: formatDuration(data.officeMinutes),
                      hint: formatPct(data.officePercentage),
                    },
                    {
                      color: '#22c55e',
                      label: 'Teletrabajo',
                      value: formatDuration(data.remoteMinutes),
                      hint: formatPct(data.remotePercentage),
                    },
                  ]
                : []
            }
          />
        }
      />
    </Panel>
  );
}

function Panel({
  title,
  loading,
  error,
  children,
}: {
  title: string;
  loading: boolean;
  error: unknown;
  children: React.ReactNode;
}) {
  return (
    <section className="border border-border rounded-sm bg-paper overflow-hidden">
      <header className="px-5 py-3.5 border-b border-border">
        <h3 className="text-base font-semibold tracking-tightish">{title}</h3>
      </header>
      <div className="px-5 py-6 min-h-[280px]">
        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : error ? (
          <p role="alert" className="text-sm text-destructive">
            {error instanceof ApiError ? error.message : 'Error cargando estadísticas'}
          </p>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

function ChartLayout({
  chart,
  legend,
}: {
  chart: React.ReactNode;
  legend: React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
      <div className="flex-shrink-0">{chart}</div>
      <div className="flex-1 w-full min-w-0">{legend}</div>
    </div>
  );
}

function Legend({
  items,
}: {
  items: { color: string; label: string; value: string; hint: string }[];
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Sin datos en el periodo.</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((it, idx) => (
        <li
          key={`${it.label}-${idx}`}
          className="flex items-center justify-between gap-3 text-[13px]"
        >
          <span className="inline-flex items-center gap-2 min-w-0 flex-1">
            <span
              className="h-2.5 w-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: it.color }}
              aria-hidden
            />
            <span className="truncate text-foreground">{it.label}</span>
          </span>
          <span className="font-mono tabular-nums text-foreground flex-shrink-0">
            {it.value}
          </span>
          <span className="font-mono tabular-nums text-muted-foreground flex-shrink-0 w-12 text-right">
            {it.hint}
          </span>
        </li>
      ))}
    </ul>
  );
}

function rangeForMonth(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${year}-${pad(month)}-01`,
    to: `${year}-${pad(month)}-${pad(lastDay)}`,
  };
}

function humanRange(year: number, month: number): string {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

function formatPct(p: number): string {
  return `${p}%`;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function periodValue(p: Period): string {
  if (p.mode === 'all') return 'all';
  if (p.mode === 'year') return `year-${p.year}`;
  return `month-${p.year}-${p.month}`;
}

function parsePeriodValue(v: string): Period {
  if (v === 'all') return { mode: 'all' };
  if (v.startsWith('year-')) {
    return { mode: 'year', year: Number(v.slice(5)) };
  }
  const [, y, m] = v.split('-');
  return { mode: 'month', year: Number(y), month: Number(m) };
}
